import React, {useEffect, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {Avatar} from "primereact/avatar";
import {useTranslate} from "../useTranslate";
import Icon from "./icon";
import BoxHeader from "../../pages/components/box-header";

function ArchiveEntityControl({
    entityName,
    sectionTitle,
    title,
    description,
    buttonLabel,
    dialogTitle,
    onArchive,
}) {
    const translate = useTranslate();
    const [visible, setVisible] = useState(false);
    const [confirmation, setConfirmation] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");
    const expectedName = String(entityName || "");
    const confirmed = Boolean(expectedName) && confirmation === expectedName;

    useEffect(() => {
        if (!visible) {
            setConfirmation("");
            setError("");
        }
    }, [visible]);

    const archive = async () => {
        if (!confirmed || pending) return;
        setPending(true);
        setError("");
        try {
            const result = await onArchive?.();
            if (!result) {
                setError(translate("archive-confirm.error"));
                return;
            }
            setVisible(false);
        } catch (archiveError) {
            const errorKey = archiveError?.message;
            const translatedError = errorKey ? translate(errorKey) : "";
            setError(
                translatedError && translatedError !== errorKey
                    ? translatedError
                    : translate("archive-confirm.error"),
            );
        } finally {
            setPending(false);
        }
    };

    const footer = (
        <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300 w-full">
            <Button
                type="button"
                className="text-xs p-button-secondary p-button-outlined border-round-lg p-2"
                label={translate("cancel")}
                disabled={pending}
                onClick={() => setVisible(false)}
            />
            <Button
                type="button"
                severity="danger"
                className="text-xs flex gap-2 border-round-lg p-2"
                icon={<Icon icon="mi mi:archive"/>}
                label={buttonLabel}
                disabled={!confirmed || pending}
                loading={pending}
                onClick={archive}
            />
        </div>
    );

    return (
        <div className="flex flex-column gap-2">
            {sectionTitle && (
                <BoxHeader
                    title={sectionTitle}
                    icon={<Icon icon="mi mi:settings_suggest" className="text-primary"/>}
                />
            )}
            <div className="flex border-1 border-round-lg border-red-500 bg-red-50 p-3 align-items-center gap-3">
                <Avatar
                    className="bg-red-500 text-white flex-shrink-0"
                    size="large"
                    shape="circle"
                    icon={<Icon icon="mi mi:archive"/>}
                />
                <div className="flex-1 flex flex-column gap-2 min-w-0">
                    <span className="font-bold text-sm text-red-600">{title}</span>
                    <span className="text-xs text-red-600 line-height-3">{description}</span>
                    <Button
                        type="button"
                        severity="danger"
                        className="text-xs gap-2 align-self-end"
                        icon={<Icon icon="mi mi:archive"/>}
                        label={buttonLabel}
                        onClick={() => setVisible(true)}
                    />
                </div>
            </div>

            <Dialog
                visible={visible}
                className="rtl w-6 border-round-bottom-2xl"
                header={(
                    <div className="flex flex-column">
                        <span className="text-lg">{dialogTitle}</span>
                        <span className="text-xs text-400">{expectedName}</span>
                    </div>
                )}
                footer={footer}
                closable={!pending}
                closeOnEscape={!pending}
                contentClassName="p-4"
                headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
                onHide={() => {
                    if (!pending) setVisible(false);
                }}
                pt={{
                    footer: () => ({className: "p-0 border-round-bottom-2xl"}),
                    content: () => ({className: "p-0 h-full"}),
                }}
            >
                <div className="flex flex-column gap-3 w-full p-4">
                    <div className="flex align-items-start gap-2">
                        <Icon icon="mi mi:warning" className="text-orange-500 text-xl flex-shrink-0"/>
                        <span className="text-xs line-height-3">{translate("archive-confirm.warning")}</span>
                    </div>
                    <label className="text-xs line-height-3" htmlFor="archive-confirmation-name">
                        {translate("archive-confirm.instruction").replace("{{name}}", expectedName)}
                    </label>
                    <InputText
                        id="archive-confirmation-name"
                        autoComplete="off"
                        className="w-full text-xs"
                        value={confirmation}
                        disabled={pending}
                        onChange={(event) => setConfirmation(event.target.value)}
                    />
                    {error && <Message severity="error" className="text-xs justify-content-start" text={error}/>}
                </div>
            </Dialog>
        </div>
    );
}

export default ArchiveEntityControl;
